import { useCallback, useMemo } from 'react';
import {
  useGetMappingLocationsQuery,
  useGetMappingStockQuery,
  useGetMappingCorridorsQuery
} from '@/entities/dashboard';
import { Warehouse, Truck, Users, MapPin } from 'lucide-react';

export function useMapping() {
  const { data: locationsData, isLoading: locationsLoading, error: locationsError } = useGetMappingLocationsQuery();
  const { data: stockData, isLoading: stockLoading, error: stockError } = useGetMappingStockQuery(undefined);
  const {
    data: corridorsData,
    isLoading: corridorsLoading,
    error: corridorsError
  } = useGetMappingCorridorsQuery({ type: 'RIT' });

  const isLoading = locationsLoading || stockLoading || corridorsLoading;
  const error = locationsError || stockError || corridorsError;

  const getLocationIcon = useCallback((type: string) => {
    switch (type) {
      case 'W':
        return Warehouse;
      case 'P':
        return Truck;
      case 'C':
        return Users;
      case 'S':
        return MapPin;
      default:
        return MapPin;
    }
  }, []);

  const getLocationTypeColor = useCallback((type: string) => {
    switch (type) {
      case 'W':
        return 'bg-blue-500';
      case 'P':
        return 'bg-green-500';
      case 'C':
        return 'bg-orange-500';
      case 'S':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  }, []);

  const formatNumber = useCallback((num: number) => num.toLocaleString(), []);

  const stats = useMemo(() => {
    if (!locationsData) return null;

    return {
      totalLocations: locationsData.locations.length,
      activeRoutes: corridorsData?.features.length || 0,
      coverageAreas: new Set(locationsData.locations.map((l) => l.type)).size,
      warehouseCount: locationsData.locations.filter((l) => l.type === 'W').length,
      distributionPointCount: locationsData.locations.filter((l) => l.type === 'P').length,
      customerCount: locationsData.locations.filter((l) => l.type === 'C').length,
      siteCount: locationsData.locations.filter((l) => l.type === 'S').length
    };
  }, [locationsData, corridorsData]);

  const locationsByType = useMemo(() => {
    if (!locationsData) return { warehouses: [], distributionPoints: [], customers: [], sites: [] };

    return {
      warehouses: locationsData.locations.filter((l) => l.type === 'W'),
      distributionPoints: locationsData.locations.filter((l) => l.type === 'P'),
      customers: locationsData.locations.filter((l) => l.type === 'C'),
      sites: locationsData.locations.filter((l) => l.type === 'S')
    };
  }, [locationsData]);

  // Combine stock data with location data
  const locationsWithStock = useMemo(() => {
    if (!locationsData || !stockData) return [];

    return locationsData.locations.map((location) => {
      const stock = stockData.features.find((f) => f.id === location.id);
      return {
        ...location,
        stockValue: stock?.y || 0,
        stockProgress: stock?.progress || []
      };
    });
  }, [locationsData, stockData]);

  // Process corridor data for visualization
  const processedCorridors = useMemo(() => {
    if (!corridorsData || !locationsData) return [];

    return corridorsData.features.map((corridor) => {
      const fromLocation = locationsData.locations.find((l) => l.id === corridor.from);
      const toLocation = locationsData.locations.find((l) => l.id === corridor.to);

      return {
        ...corridor,
        fromLocation,
        toLocation
      };
    });
  }, [corridorsData, locationsData]);

  return {
    locationsData,
    stockData,
    corridorsData,
    locationsWithStock,
    processedCorridors,
    stats,
    locationsByType,
    isLoading,
    error,
    getLocationIcon,
    getLocationTypeColor,
    formatNumber
  };
}
